import { Plus, ScanBarcode, X } from "lucide-react";
import { useRef, useState } from "react";
import { Button, IconButton } from "../../primitives/Button";
import { Input } from "../../primitives/Input";
import { useUiText } from "../../primitives/uiText";
import { BarcodeScanField } from "./BarcodeScanField";

/**
 * A SKU's barcodes, one per row (ADJ-1022-1): add a row, type or scan into it,
 * take one out. The console's item form and the store portal's catalogue both
 * edit the same `barcodes` list with it.
 *
 * **Scan appends.** The scan field stays open after a scan, because somebody
 * adding a supplier's barcode and a retailer's has the next box in hand; a code
 * already in the list is not added twice (the server would drop it anyway).
 *
 * **A row error is the caller's.** Only the caller knows which row the server
 * meant by `barcode_taken` (it looks the codes up); this draws the sentence
 * under that row and marks its input invalid.
 */

/** The server's `MaxBarcodesPerSku`. */
export const MAX_BARCODES = 8;

export interface BarcodeListFieldProps {
	value: string[];
	onChange: (next: string[]) => void;
	/** A sentence under a row, by its index. */
	rowErrors?: Record<number, string> | undefined;
	max?: number;
	disabled?: boolean;
	/** From `Field`: the label points at the first row (not at Add, whose own words name it). */
	id?: string;
	"aria-describedby"?: string | undefined;
}

/** The list as it is sent: trimmed, upper-cased like the server, no blanks or repeats. */
export function cleanBarcodes(value: string[]): string[] {
	const out: string[] = [];
	for (const raw of value) {
		const code = raw.trim().toUpperCase();
		if (code !== "" && !out.includes(code)) {
			out.push(code);
		}
	}
	return out;
}

export function BarcodeListField({
	value,
	onChange,
	rowErrors,
	max = MAX_BARCODES,
	disabled = false,
	id,
	"aria-describedby": describedBy,
}: BarcodeListFieldProps) {
	const t = useUiText();
	const [scanning, setScanning] = useState(false);
	const list = useRef<HTMLUListElement>(null);
	const full = value.length >= max;

	function add() {
		onChange([...value, ""]);
		// The new row is the one being typed into.
		requestAnimationFrame(() =>
			list.current?.querySelector<HTMLInputElement>("li:last-child input")?.focus(),
		);
	}

	function scanned(code: string) {
		const clean = code.trim().toUpperCase();
		if (cleanBarcodes(value).includes(clean) || full) {
			return;
		}
		// A blank row left by Add is filled rather than kept beside the scan.
		const blank = value.findIndex((one) => one.trim() === "");
		onChange(blank < 0 ? [...value, clean] : value.map((one, i) => (i === blank ? clean : one)));
	}

	return (
		<div className="flex flex-col gap-2">
			{value.length > 0 && (
				<ul ref={list} className="flex flex-col gap-2">
					{value.map((code, i) => {
						const error = rowErrors?.[i];
						const n = i + 1;
						return (
							// Rows have no identity but their place; a removal re-keys
							// the rest, which only re-renders inputs with their values.
							// biome-ignore lint/suspicious/noArrayIndexKey: see above
							<li key={i} className="flex flex-col gap-1">
								<div className="flex items-center gap-2">
									<Input
										{...(i === 0 && id !== undefined ? { id } : {})}
										aria-label={t("ui.barcodes.row", { n })}
										aria-describedby={error === undefined ? describedBy : `${id ?? "bc"}-err-${i}`}
										aria-invalid={error === undefined ? undefined : true}
										className="font-mono"
										value={code}
										disabled={disabled}
										autoComplete="off"
										spellCheck={false}
										onChange={(event) =>
											onChange(value.map((one, j) => (j === i ? event.target.value : one)))
										}
									/>
									<IconButton
										icon={X}
										variant="ghost"
										label={t("ui.barcodes.remove", { n })}
										disabled={disabled}
										onClick={() => onChange(value.filter((_, j) => j !== i))}
									/>
								</div>
								{error !== undefined && (
									<p id={`${id ?? "bc"}-err-${i}`} className="text-[11.5px] text-danger-fg">
										{error}
									</p>
								)}
							</li>
						);
					})}
				</ul>
			)}
			{scanning && !disabled && (
				<BarcodeScanField
					onScan={scanned}
					placeholder={t("ui.barcodes.scanPlaceholder")}
					aria-label={t("ui.barcodes.scan")}
				/>
			)}
			<div className="flex flex-wrap items-center gap-2">
				<Button variant="secondary" size="sm" icon={Plus} disabled={disabled || full} onClick={add}>
					{t("ui.barcodes.add")}
				</Button>
				<Button
					variant="secondary"
					size="sm"
					icon={ScanBarcode}
					disabled={disabled || (full && !scanning)}
					aria-pressed={scanning}
					onClick={() => setScanning((on) => !on)}
				>
					{t(scanning ? "ui.barcodes.scanDone" : "ui.barcodes.scan")}
				</Button>
				{full && (
					<span className="text-[12px] text-fg-muted">{t("ui.barcodes.limit", { max })}</span>
				)}
			</div>
		</div>
	);
}
